/* eslint-disable no-restricted-syntax */
import { Children, cloneElement, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useURLQuery } from '../../Common/Hook/urlQuery';
import { useInterviewsGetQuery } from '../../Common/Queries/interviewsQueries';

import { isValidID } from '../../utils';
import { getAllInterviewMap } from '../../utils/responseMap.ts';

import { AuthContext } from '../../constants';

export const MyInterviewsContainer = ({ children }) => {
  const urlQuery = useURLQuery();
  const { userInfo } = useContext(AuthContext);
  const location = useLocation();

  const userId =
    (isValidID(urlQuery.get('userID')) && urlQuery.get('userID')) || userInfo?.entityID;

  const isPreviousPath = (location?.pathname || '').includes('previous-interviews');

  const view =
    urlQuery.get('view') ||
    (isPreviousPath || urlQuery.get('past-interviews-page')
      ? 'previous-interviews'
      : 'upcoming-interviews');

  const status = urlQuery.get('status');
  const skipRounds = urlQuery.get('skipRounds');
  const exemptAppStatus = urlQuery.get('exemptAppStatus');

  const initialSort = urlQuery.get('sort') || 'ASC';
  const initialOrderBy = urlQuery.get('orderBy') || 'startTime';

  const startTimeFrom = urlQuery.get('startTimeRange.start-time') || '';
  const startTimeTo = urlQuery.get('startTimeRange.end-time') || '';

  // Separate page states like the old code
  const initialUpcomingPage = Number(urlQuery.get('upcoming-interviews-page')) || 1;
  const initialPastPage = Number(urlQuery.get('past-interviews-page')) || 1;

  const [filters, setFilters] = useState({
    upcomingInterviewsPage: initialUpcomingPage - 1,
    pastInterviewsPage: initialPastPage - 1,
    sort: initialSort,
    orderBy: initialOrderBy,
  });

  // Sync to URL when filters change
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);

      params.set('view', view);
      params.set('upcoming-interviews-page', String(filters.upcomingInterviewsPage + 1));
      params.set('past-interviews-page', String(filters.pastInterviewsPage + 1));
      params.set('sort', filters.sort);
      params.set('orderBy', filters.orderBy);

      // Set the current view's page as the main 'page' param
      if (view === 'upcoming-interviews') {
        params.set('page', String(filters.upcomingInterviewsPage + 1));
      } else {
        params.set('page', String(filters.pastInterviewsPage + 1));
      }

      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    } catch (e) {
      console.warn('URL sync failed', e);
    }
  }, [
    filters.upcomingInterviewsPage,
    filters.pastInterviewsPage,
    filters.sort,
    filters.orderBy,
    view,
  ]);

  const handleOnUpcomingFilterChange = (updatedFilters) => {
    setFilters((prev) => {
      let nextPage = prev.upcomingInterviewsPage;
      let nextSort = prev.sort;
      let nextOrderBy = prev.orderBy;

      if (updatedFilters.page !== undefined) {
        nextPage = updatedFilters.page;
      }

      if (updatedFilters.order !== undefined && updatedFilters.orderBy !== undefined) {
        const incomingOrder = updatedFilters.order.toUpperCase();

        if (incomingOrder === prev.sort && updatedFilters.orderBy === prev.orderBy) {
          nextSort = prev.sort === 'ASC' ? 'DESC' : 'ASC';
        } else {
          nextSort = incomingOrder;
          nextOrderBy = updatedFilters.orderBy;
        }

        nextPage = 0;
      }

      return {
        ...prev,
        upcomingInterviewsPage: nextPage,
        sort: nextSort,
        orderBy: nextOrderBy,
      };
    });
  };

  const handleOnPastFilterChange = (updatedFilters) => {
    setFilters((prev) => {
      let nextPage = prev.pastInterviewsPage;
      let nextSort = prev.sort;
      let nextOrderBy = prev.orderBy;

      if (updatedFilters.page !== undefined) {
        nextPage = updatedFilters.page;
      }

      if (updatedFilters.order !== undefined && updatedFilters.orderBy !== undefined) {
        const incomingOrder = updatedFilters.order.toUpperCase();

        if (incomingOrder === prev.sort && updatedFilters.orderBy === prev.orderBy) {
          nextSort = prev.sort === 'ASC' ? 'DESC' : 'ASC';
        } else {
          nextSort = incomingOrder;
          nextOrderBy = updatedFilters.orderBy;
        }

        nextPage = 0;
      }

      return {
        ...prev,
        pastInterviewsPage: nextPage,
        sort: nextSort,
        orderBy: nextOrderBy,
      };
    });
  };

  const upcomingReqParams = useMemo(
    () => ({
      page: filters.upcomingInterviewsPage + 1,
      limit: 20,
      interviewerID: userId,
      view: 'upcoming-interviews',
      status,
      skipRounds,
      sort: filters.sort,
      orderBy: filters.orderBy,
      'startTimeRange.start-time': startTimeFrom,
      'startTimeRange.end-time': startTimeTo,
      source: urlQuery.get('source'),
    }),
    [
      filters.upcomingInterviewsPage,
      filters.sort,
      filters.orderBy,
      userId,
      status,
      skipRounds,
      startTimeFrom,
      startTimeTo,
      urlQuery,
    ]
  );

  const pastReqParams = useMemo(
    () => ({
      page: filters.pastInterviewsPage + 1,
      limit: 20,
      interviewerID: userId,
      view: 'previous-interviews',
      status,
      skipRounds,
      exemptAppStatus,
      sort: filters.sort,
      orderBy: filters.orderBy,
      'startTimeRange.start-time': startTimeFrom,
      'startTimeRange.end-time': startTimeTo,
      source: urlQuery.get('source'),
    }),
    [
      filters.pastInterviewsPage,
      filters.sort,
      filters.orderBy,
      userId,
      status,
      skipRounds,
      exemptAppStatus,
      startTimeFrom,
      startTimeTo,
      urlQuery,
    ]
  );

  const upcomingInterviews = useInterviewsGetQuery(upcomingReqParams, {
    select: getAllInterviewMap,
    enabled: Boolean(userId) && view === 'upcoming-interviews',
  });

  const pastInterviews = useInterviewsGetQuery(pastReqParams, {
    select: getAllInterviewMap,
    enabled: Boolean(userId) && view === 'previous-interviews',
  });

  const child = Children.only(children);

  return cloneElement(child, {
    upcomingInterviews: view === 'upcoming-interviews' ? upcomingInterviews : undefined,
    pastInterviews: view === 'previous-interviews' ? pastInterviews : undefined,
    filters,
    filterHandlerUpcoming: handleOnUpcomingFilterChange,
    filterHandlerPast: handleOnPastFilterChange,
  });
};
